/**
 * =============================================================================
 * Página: /afiliado/vincular-programa/:tenantSlug/:programSlug
 * Versão: 1.0.0 (Plano v2 — Multi-Tenant para Afiliados)
 *
 * Descrição:
 *   Permite que um usuário JÁ AUTENTICADO se vincule a um NOVO programa de
 *   indicação (em outro tenant ou no mesmo tenant). Substitui o anti-padrão
 *   de fazer signInWithPassword dentro da Edge Function affiliate-signup.
 *
 * Fluxo:
 *   1. Tela exibe nome do programa, termos e formulário enxuto (telefone/doc).
 *   2. Ao confirmar, chama a RPC `link_affiliate_to_existing_user` (auth.uid).
 *   3. Após sucesso, chama `set_active_affiliate(novo_id)` e redireciona.
 *
 * Segurança:
 *   - Toda RPC é SECURITY DEFINER e usa auth.uid() — frontend NUNCA passa
 *     user_id.
 *   - Programa é resolvido por slugs públicos (RPC get_public_program_by_slugs).
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Loader2,
  ShieldCheck,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { applyPhoneMask, applyCPFMask, applyCNPJMask } from '@/lib/input-masks';
import {
  validatePhone,
  validateCPF,
  validateCNPJ,
  validateFullName,
} from '@/lib/input-validators';
import { sanitizeHtml } from '@/lib/sanitize';
import { affiliateAwaitingPath } from '@/lib/affiliate-urls';
import { createLogger } from '@/lib/logger';

const log = createLogger('AffiliateLinkProgram');

// -----------------------------------------------------------------------------
// Tipos do programa (vindos de get_public_program_by_slugs)
// -----------------------------------------------------------------------------
interface PublicProgram {
  program_id: string;
  program_name: string;
  program_slug: string;
  tenant_id: string;
  description: string | null;
  terms_html: string | null;
  require_terms_acceptance: boolean;
  auto_approve_affiliates: boolean;
  /** Hidratado pela página a partir da rota / query do tenant. */
  tenant_name?: string;
}

export default function AffiliateLinkProgram() {
  const { tenantSlug, programSlug } = useParams<{
    tenantSlug: string;
    programSlug: string;
  }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, ready, refresh } = useAffiliateAuth();

  // ---------------------------------------------------------------------------
  // Estado da página
  // ---------------------------------------------------------------------------
  const [program, setProgram] = useState<PublicProgram | null>(null);
  const [loadingProgram, setLoadingProgram] = useState(true);
  const [programError, setProgramError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    full_name: user?.user_metadata?.full_name ?? '',
    phone: '',
    document_number: '',
    document_type: 'cpf' as 'cpf' | 'cnpj',
    terms_accepted: false,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // ---------------------------------------------------------------------------
  // Carrega o programa pelos slugs (mesma RPC do cadastro público)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!tenantSlug || !programSlug) {
      setProgramError('Programa inválido');
      setLoadingProgram(false);
      return;
    }
    (async () => {
      const { data, error } = await supabase.rpc('get_public_program_by_slugs', {
        p_tenant_slug: tenantSlug,
        p_program_slug: programSlug,
      });
      if (error || !data || data.length === 0) {
        log.warn('Programa não encontrado', { tenantSlug, programSlug, error });
        setProgramError('Programa não encontrado ou inativo.');
      } else {
        setProgram(data[0] as unknown as PublicProgram);
      }
      setLoadingProgram(false);
    })();
  }, [tenantSlug, programSlug]);

  // Sincroniza nome quando o user chega
  useEffect(() => {
    const meta = user?.user_metadata?.full_name;
    if (meta && !formData.full_name) {
      setFormData((s) => ({ ...s, full_name: meta }));
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const sanitizedTerms = useMemo(
    () => (program?.terms_html ? sanitizeHtml(program.terms_html) : ''),
    [program?.terms_html],
  );

  // ---------------------------------------------------------------------------
  // Validação
  // ---------------------------------------------------------------------------
  function runValidation(): Record<string, string> {
    const errs: Record<string, string> = {};
    const nameRes = validateFullName(formData.full_name);
    if (!nameRes.isValid) errs.full_name = nameRes.error!;

    if (formData.phone) {
      const phoneRes = validatePhone(formData.phone);
      if (!phoneRes.isValid) errs.phone = phoneRes.error!;
    }
    if (formData.document_number) {
      const docRes =
        formData.document_type === 'cpf'
          ? validateCPF(formData.document_number)
          : validateCNPJ(formData.document_number);
      if (!docRes.isValid) errs.document_number = docRes.error!;
    }
    if (program?.require_terms_acceptance && !formData.terms_accepted) {
      errs.terms_accepted = 'Você precisa aceitar os termos para continuar';
    }
    return errs;
  }

  // ---------------------------------------------------------------------------
  // Submit → RPC link_affiliate_to_existing_user + set_active_affiliate
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!program) return;
    setSubmitError(null);

    const errs = runValidation();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setSubmitError('Corrija os campos destacados antes de continuar.');
      return;
    }

    setSubmitting(true);

    const phoneDigits = formData.phone.replace(/\D/g, '');
    const docDigits = formData.document_number.replace(/\D/g, '');

    try {
      const { data, error } = await supabase.rpc(
        'link_affiliate_to_existing_user',
        {
          p_program_id: program.program_id,
          p_full_name: formData.full_name.trim(),
          p_phone: phoneDigits || null,
          p_document_number: docDigits || null,
          p_document_type: docDigits ? formData.document_type : null,
          p_terms_accepted: formData.terms_accepted,
        },
      );

      if (error) {
        log.error('link_affiliate_to_existing_user erro', error);
        setSubmitError(
          (error as { message?: string }).message ??
            'Não foi possível vincular ao programa.',
        );
        setSubmitting(false);
        return;
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.affiliate_id) {
        setSubmitError('Resposta inválida do servidor.');
        setSubmitting(false);
        return;
      }

      // Persiste a conta ativa no servidor antes de navegar
      await supabase.rpc('set_active_affiliate', {
        p_affiliate_id: row.affiliate_id,
      });

      // Recarrega lista de afiliados no contexto
      await refresh();

      setSuccess(true);
      const auto = row.auto_approved === true;
      toast({
        title: auto ? 'Vinculado com sucesso!' : 'Solicitação recebida',
        description: auto
          ? 'Você já tem acesso ao novo programa.'
          : 'Aguarde a aprovação do administrador deste programa.',
      });
      setTimeout(
        () =>
          navigate(auto ? '/afiliado' : affiliateAwaitingPath(tenantSlug), {
            replace: true,
          }),
        600,
      );
    } catch (err) {
      log.error('handleSubmit exception', err);
      setSubmitError(
        err instanceof Error ? err.message : 'Erro inesperado',
      );
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render — guards
  // ---------------------------------------------------------------------------
  if (!ready || loadingProgram) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (programError || !program) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-center">Programa indisponível</CardTitle>
            <CardDescription className="text-center">
              {programError ?? 'Programa não encontrado.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/afiliado">Voltar ao painel</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — formulário
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 p-4 flex items-center justify-center">
      <Card className="w-full max-w-2xl shadow-xl border-border/50">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Vincular novo programa</CardTitle>
          <CardDescription>
            Confirme os dados abaixo para participar do programa{' '}
            <span className="font-semibold text-foreground">
              {program.program_name}
            </span>
            .
          </CardDescription>
        </CardHeader>

        <CardContent>
          {success ? (
            <div className="flex flex-col items-center text-center py-8 gap-3">
              <CheckCircle2 className="h-12 w-12 text-primary" />
              <p className="font-medium">Tudo certo!</p>
              <p className="text-sm text-muted-foreground">
                Redirecionando...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {submitError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="full_name">Nome completo *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData((s) => ({
                      ...s,
                      full_name: e.target.value.replace(/[<>]/g, ''),
                    }))
                  }
                  placeholder="Seu nome completo"
                  maxLength={120}
                  required
                  disabled={submitting}
                />
                {fieldErrors.full_name && (
                  <p className="text-xs text-destructive">
                    {fieldErrors.full_name}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData((s) => ({
                        ...s,
                        phone: applyPhoneMask(e.target.value),
                      }))
                    }
                    placeholder="(11) 99999-9999"
                    inputMode="tel"
                    maxLength={16}
                    autoComplete="tel"
                    disabled={submitting}
                  />
                  {fieldErrors.phone && (
                    <p className="text-xs text-destructive">
                      {fieldErrors.phone}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="document_number">CPF / CNPJ</Label>
                  <div className="flex gap-2">
                    <select
                      className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                      value={formData.document_type}
                      onChange={(e) =>
                        setFormData((s) => ({
                          ...s,
                          document_type: e.target.value as 'cpf' | 'cnpj',
                          document_number: '',
                        }))
                      }
                      disabled={submitting}
                    >
                      <option value="cpf">CPF</option>
                      <option value="cnpj">CNPJ</option>
                    </select>
                    <Input
                      id="document_number"
                      value={formData.document_number}
                      onChange={(e) =>
                        setFormData((s) => ({
                          ...s,
                          document_number:
                            formData.document_type === 'cpf'
                              ? applyCPFMask(e.target.value)
                              : applyCNPJMask(e.target.value),
                        }))
                      }
                      placeholder={
                        formData.document_type === 'cpf'
                          ? '000.000.000-00'
                          : '00.000.000/0000-00'
                      }
                      inputMode="numeric"
                      maxLength={
                        formData.document_type === 'cpf' ? 14 : 18
                      }
                      disabled={submitting}
                    />
                  </div>
                  {fieldErrors.document_number && (
                    <p className="text-xs text-destructive">
                      {fieldErrors.document_number}
                    </p>
                  )}
                </div>
              </div>

              {program.terms_html && (
                <div className="space-y-2">
                  <Label>Termos do programa</Label>
                  <ScrollArea className="h-40 rounded-md border p-3 text-sm">
                    <div
                      className="prose prose-sm max-w-none dark:prose-invert"
                      dangerouslySetInnerHTML={{ __html: sanitizedTerms }}
                    />
                  </ScrollArea>
                </div>
              )}

              {program.require_terms_acceptance && (
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="terms_accepted"
                    checked={formData.terms_accepted}
                    onCheckedChange={(c) =>
                      setFormData((s) => ({
                        ...s,
                        terms_accepted: c === true,
                      }))
                    }
                    disabled={submitting}
                  />
                  <Label
                    htmlFor="terms_accepted"
                    className="text-sm leading-snug cursor-pointer"
                  >
                    Li e aceito os termos deste programa.
                  </Label>
                </div>
              )}
              {fieldErrors.terms_accepted && (
                <p className="text-xs text-destructive">
                  {fieldErrors.terms_accepted}
                </p>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate('/afiliado', { replace: true })}
                  disabled={submitting}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Vinculando...
                    </>
                  ) : (
                    <>
                      Confirmar vínculo
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
